'use client';

import {headerVariants, microHoverVariants} from "@/lib/animations";
import {motion} from "framer-motion";
import React, {useState, useRef, useEffect} from "react";
import Image from '@/components/ui/MediaImage';
import {Autoplay} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/react";
import type {Swiper as SwiperType} from "swiper";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { transformMediaUrl } from "@/lib/media-url-transformer";

interface ReviewData {
    id: number;
    name: string;
    title: string | null;
    comment: string;
    rating: number | null;
    image: string | null;
    submitted_at: string | null;
}

interface YachtReviewsProps {
    /**
     * Reviews verisi (server-side'dan props ile gelir)
     * Eğer sağlanmazsa ve enableClientFetch true ise client-side'da çekilir
     */
    reviews?: ReviewData[];
    /**
     * Review ID'leri (client-side fetch için)
     * Eğer reviews props olarak sağlanmışsa, bu prop kullanılmaz
     */
    reviewIds?: number[];
    reviewableType?: string;
    reviewableId?: number;
    siteId?: number;
    languageId?: number;
    language?: string;
    languageCode?: string;
    /**
     * Sol resim (media ID, URL veya obje formatında)
     */
    leftImage?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
    /**
     * Sağ resim (media ID, URL veya obje formatında)
     */
    rightImage?: number | string | { mediaId?: number; url?: string; alt?: string } | null;
    /**
     * Başlık ve açıklama metinleri
     */
    subTitle?: string;
    title?: string;
    description?: string;
    /**
     * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
     * GrapeJS preview için kullanışlıdır
     */
    enableClientFetch?: boolean;
    __pageContext?: {
        siteId?: number;
        languageId?: number;
        language?: string;
        languageCode?: string;
    };
}

// Fallback görseller (yorum görseli yoksa kullanılacak)
const FALLBACK_IMAGES = [
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=450&h=380&fit=crop",
    "https://images.unsplash.com/photo-1500375592092-40eb2168fd21?w=450&h=380&fit=crop"
];

export default function YachtReviews(props: YachtReviewsProps = {}) {
    const {
        reviews: initialReviews,
        reviewIds,
        reviewableType,
        reviewableId,
        siteId: propSiteId,
        languageId: propLanguageId,
        language,
        languageCode,
        leftImage,
        rightImage,
        subTitle: propSubTitle,
        title: propTitle,
        description: propDescription,
        enableClientFetch = false,
        __pageContext
    } = props;

    const [reviews, setReviews] = useState<ReviewData[]>(initialReviews || []);
    const [loading, setLoading] = useState(!initialReviews);
    const [error, setError] = useState<string | null>(null);
    const [activePage, setActivePage] = useState(1);
    const [slidesPerView, setSlidesPerView] = useState(3);
    const swiperRef = useRef<SwiperType | null>(null);
    
    // Language variables için state
    const [variables, setVariables] = useState<Record<string, string>>({});
    
    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };

    // Language ID'yi belirle (öncelik sırası: prop > pageContext)
    const languageId = propLanguageId || __pageContext?.languageId;

    // Props'tan veri geldiyse client-side fetch yapma
    const hasInitialData = initialReviews && initialReviews.length > 0;

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    const { variables: contextVariables } = useLanguageVariables();
    
    // Context'ten gelen variables'ı kullan, yoksa boş object
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    // Reviews fetch (eğer initialReviews yoksa ve enableClientFetch true ise)
    useEffect(() => {
        if (hasInitialData || !enableClientFetch) {
            return;
        }

        const fetchReviews = async () => {
            setLoading(true);
            setError(null);

            try {
                let finalLanguage: string | undefined = language || languageCode || __pageContext?.language || __pageContext?.languageCode;
                
                if (!finalLanguage && typeof window !== 'undefined') {
                    const pathParts = window.location.pathname.split('/').filter(Boolean);
                    const possibleLang = pathParts[0];
                    if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
                        finalLanguage = possibleLang;
                    }
                }

                const params = new URLSearchParams();
                if (reviewIds && reviewIds.length > 0) {
                    reviewIds.forEach(id => params.append('review_ids[]', String(id)));
                } else if (reviewableType && reviewableId) {
                    params.append('reviewable_type', reviewableType);
                    params.append('reviewable_id', String(reviewableId));
                }
                if (finalLanguage) {
                    params.append('language', finalLanguage);
                }

                const response = await fetch(`/api/reviews/list-simple?${params.toString()}`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch reviews: ${response.status}`);
                }

                const data = await response.json();
                if (data.success && Array.isArray(data.reviews)) {
                    setReviews(data.reviews);
                } else {
                    setReviews([]);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load reviews');
                console.error('[YachtReviews] Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, [enableClientFetch, reviewIds, reviewableType, reviewableId, language, languageCode, __pageContext?.language, __pageContext?.languageCode, hasInitialData]);

    // Resim URL'lerini hazırla
    const getImageUrl = (imageProp: number | string | { mediaId?: number; url?: string } | null | undefined): string | null => {
        if (!imageProp) return null;
        if (typeof imageProp === 'object' && 'url' in imageProp) {
            return imageProp.url || null;
        } else if (typeof imageProp === 'string') {
            return imageProp;
        } else if (typeof imageProp === 'number') {
            return null;
        }
        return null;
    };

    // Loading state
    if (loading) {
        return (
            <section className="w-full px-4 lg:px-0 lg:w-8/12 mx-auto">
                <div className="text-center text-gray-500 py-20">{getVariable('reviews_loading', 'Loading reviews...')}</div>
            </section>
        );
    }

    // Error state
    if (error) {
        return (
            <section className="w-full px-4 lg:px-0 lg:w-8/12 mx-auto">
                <div className="text-center text-red-500 py-20">{getVariable('reviews_error_prefix', 'Error: ')}{error}</div>
            </section>
        );
    }

    // Reviews'i formatla
    const formattedReviews = reviews.length > 0 
        ? reviews.map((review) => ({
            user: {
                name: review.name,
                title: review.title || '',
                img: review.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(review.name)}&size=150&background=random`
            },
            message: review.comment,
        }))
        : [
            {
                user: {
                    name: "Piter Bowman",
                    title: "Business CEO",
                    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
                },
                message: "Leverage agile frameworks to provide a robust synopsis for high level overviews. Iterative in approaches to corporate strategy data foster go to collaborative thinking.",
            },
            {
                user: {
                    name: "Ayşe Demir",
                    title: "Ankara",
                    img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
                },
                message: "Leverage agile frameworks to provide a robust synopsis for high level overviews. Iterative in approaches to corporate strategy data foster go to collaborative thinking.",
            },
            {
                user: {
                    name: "Mehmet Kaya",
                    title: "İzmir",
                    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                },
                message: "Leverage agile frameworks to provide a robust synopsis for high level overviews. Iterative in approaches to corporate strategy data foster go to collaborative thinking.",
            }
        ];

    const subTitle = propSubTitle || getVariable('reviews_subtitle', 'Comment');
    const title = propTitle || getVariable('reviews_title', 'The Perfect Boat Rental Adventure');
    const description = propDescription || getVariable('reviews_description', 'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusant doloremque laudatium totam rem aperiam vitae ullamcorper. Ut enim ad minim.');

    return (
        <section className="w-full px-4 lg:px-0 lg:w-8/12 mx-auto">
            <div className="w-7/12 mx-auto ">
                <motion.div className="w-full flex flex-col gap-6 mb-10 items-center text-center" variants={headerVariants}>
                    <motion.span 
                        className="font-display font-medium text-alsero-first text-xl sm:text-2xl md:text-3xl lg:text-4xl" 
                        variants={microHoverVariants} 
                        initial="rest" 
                        whileHover="hover"
                        data-prop-path="subTitle"
                    >
                        {subTitle}
                    </motion.span>
                    <motion.h2 
                        className="font-bold text-alsero-text-dark text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight sm:leading-tight md:leading-snug"
                        data-prop-path="title"
                    >
                        {title}
                    </motion.h2>
                    <p 
                        className="text-alsero-text-gray text-base sm:text-lg md:text-xl leading-relaxed max-w-prose"
                        data-prop-path="description"
                    >
                        {description}
                    </p>
                </motion.div>
            </div>
            <Swiper
                modules={[Autoplay]}
                spaceBetween={24}
                slidesPerView={slidesPerView}
                loop={false}
                initialSlide={1}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                centeredSlides={true}
                className="w-full h-full"
                breakpoints={{
                    320: {
                        slidesPerView: 1,
                        spaceBetween: 16,
                    },
                    768: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                    },
                    1024: {
                        slidesPerView: 3,
                        spaceBetween: 24,
                    },
                }}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                    
                    const currentBreakpoint = swiper.currentBreakpoint;
                    if (currentBreakpoint === '320') {
                        setSlidesPerView(1);
                        setActivePage(0); 
                    } else if (currentBreakpoint === '768') {
                        setSlidesPerView(2);
                        setActivePage(0); 
                    } else {
                        setSlidesPerView(3);
                        setActivePage(1); 
                    }
                }}
                onSlideChange={(swiper) => {
                    const currentPage = Math.floor(swiper.activeIndex / slidesPerView);
                    setActivePage(currentPage);
                }}
                onReachEnd={() => {
                    if (swiperRef.current) {
                        swiperRef.current.autoplay.stop();
                    }
                }}
                onBreakpoint={(swiper, breakpointParams) => {
                    const newSlidesPerView = breakpointParams.slidesPerView as number;
                    setSlidesPerView(newSlidesPerView);
                    
                    if (newSlidesPerView === 1) {
                        swiper.slideTo(0);
                        setActivePage(0);
                    } else if (newSlidesPerView === 2) {
                        swiper.slideTo(0); 
                        setActivePage(0);
                    } else {
                        swiper.slideTo(1); 
                        setActivePage(1);
                    }
                }}
            >
                {formattedReviews.map((review, index) => (
                    <SwiperSlide key={index} className="w-full">
                        {({ isActive }) => (
                            <div key={index} className={`group border border-alsero-second rounded-lg py-12 px-10 px hover:bg-alsero-first hover:border-alsero-first hover:mt-5 transition-all ease-in-out duration-300 ${isActive && 'mt-5'}`}>
                                <div className="flex flex-col gap-10">
                                    <div className="flex flex-row justify-between">
                                        <div className="flex flex-row gap-2 items-center">
                                            <Image src={review.user.img} alt={review.user.name} width={60} height={60} className="rounded-full object-cover" />
                                            <div className="flex flex-col">
                                                <h4 className="text-alsero-second text-xl group-hover:text-white">{review.user.name}</h4>
                                                <p className="font-light text-alsero-second group-hover:text-white">{review.user.title}</p>
                                            </div>
                                        </div>
                                        <svg width="33" height="27" viewBox="0 0 33 27" fill="none" xmlns="http://www.w3.org/2000/svg" className="stroke-alsero-dark-gray group-hover:stroke-white">
                                            <path d="M22.6569 26.5L21.3981 26.5C21.1906 26.5 21.0199 26.4313 20.862 26.2773C20.7044 26.1237 20.6393 25.9631 20.6393 25.7725L20.6393 23.3184C20.6393 23.1277 20.7044 22.9672 20.862 22.8135C21.0199 22.6595 21.1906 22.5908 21.3981 22.5908L22.6569 22.5908C24.1752 22.5908 25.4872 22.0616 26.5651 21.0107C27.6435 19.9593 28.1911 18.6736 28.1911 17.1816L28.1911 16.5684C28.1911 15.9218 27.9532 15.358 27.4899 14.9063C27.0271 14.455 26.4549 14.2275 25.8034 14.2275L21.3981 14.2275C20.4763 14.2275 19.7104 13.9184 19.0719 13.2959C18.434 12.6739 18.1219 11.9332 18.1217 11.0459L18.1217 3.68164C18.1218 2.79423 18.434 2.05368 19.0719 1.43164C19.7105 0.809075 20.4763 0.500001 21.3981 0.500001L28.9499 0.5C29.8717 0.5 30.6375 0.809075 31.276 1.43164C31.914 2.05369 32.2262 2.79421 32.2262 3.68164L32.2262 17.1816C32.2262 18.4424 31.9752 19.6429 31.4723 20.7871C30.966 21.939 30.2841 22.9303 29.4274 23.7656C28.5706 24.601 27.5524 25.2667 26.3678 25.7617C25.3379 26.1921 24.2629 26.4345 23.1403 26.4883L22.6569 26.5ZM5.03483 26.5L3.77604 26.5C3.56857 26.5 3.39779 26.4313 3.23991 26.2773C3.08246 26.1237 3.01726 25.963 3.01726 25.7725L3.01726 23.3184C3.01726 23.1278 3.08243 22.9671 3.23991 22.8135C3.39779 22.6595 3.56857 22.5908 3.77604 22.5908L5.03483 22.5908C6.55332 22.5908 7.86604 22.0618 8.94401 21.0107C10.0223 19.9593 10.57 18.6735 10.57 17.1816L10.57 16.5684C10.57 15.9218 10.3312 15.358 9.86784 14.9063C9.405 14.455 8.83284 14.2275 8.18132 14.2275L3.77604 14.2275C2.85447 14.2275 2.08925 13.9182 1.45085 13.2959C0.812933 12.6739 0.499781 11.9332 0.499676 11.0459L0.499676 3.68164C0.499717 2.79421 0.812867 2.0537 1.45085 1.43164C2.08928 0.809304 2.85441 0.500017 3.77604 0.500002L11.3288 0.500002C12.2503 0.500105 13.0156 0.809259 13.654 1.43164C14.292 2.05369 14.6041 2.7942 14.6042 3.68164L14.6042 17.1816C14.6042 18.4424 14.3532 19.6429 13.8503 20.7871C13.3439 21.9391 12.662 22.9303 11.8053 23.7656C10.9485 24.601 9.93036 25.2667 8.74577 25.7617C7.71604 26.192 6.64151 26.4344 5.51921 26.4883L5.03483 26.5Z"/>
                                        </svg>
                                    </div>
                                    <p className="text-lg text-alsero-second group-hover:text-white">{review.message}</p>
                                </div>
                            </div>
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className="flex justify-center mt-10 gap-2">
                {Array.from({length: Math.ceil(formattedReviews.length/slidesPerView)}).map((_, index) => (
                    <button type="button"
                        key={index}
                        onClick={() => {
                            if (swiperRef.current) {
                                swiperRef.current.slideTo(index * slidesPerView);
                            }
                        }}
                        className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                            activePage === index 
                                ? 'bg-alsero-text-dark' 
                                : 'bg-alsero-soft-white'
                        }`} 
                    />
                ))}
            </div>
        </section>
    )
}